import React from 'react';
import styled from '@benzinga/themetron';
import { Icon } from '@benzinga/core-ui';
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';

export interface AuthInputProps {
  hideLabel?: boolean;
  label?: string | null;
  icon?: React.ReactNode;
  name: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type: string;
  value: string;
  disabled?: boolean;
}

export const AuthInput = ({
  disabled,
  hideLabel,
  icon,
  label,
  name,
  onChange,
  onKeyDown,
  placeholder,
  type,
  value,
  ...props
}: AuthInputProps & React.InputHTMLAttributes<HTMLInputElement>) => {
  const generatedId = React.useId();
  const inputId = props.id ?? `${name}-${generatedId}`;
  const shouldHideLabel = hideLabel ?? Boolean(icon);
  return (
    <AuthInputWrapper>
      {label ? (
        <label className={shouldHideLabel ? 'sr-only' : undefined} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {icon ? <span aria-hidden="true">{icon}</span> : null}
      <div className="input-wrapper">
        <input
          {...props}
          className="text-lg"
          disabled={disabled}
          id={inputId}
          name={name}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? label ?? undefined}
          type={type}
          value={value}
        />
      </div>
    </AuthInputWrapper>
  );
};

export type AuthPasswordInputProps = Omit<AuthInputProps, 'type'> &
  React.InputHTMLAttributes<HTMLInputElement> & {
    hidden: boolean;
    toggleHidden: () => void;
  };

export const AuthPasswordInput = ({
  disabled,
  hidden,
  hideLabel,
  icon,
  label,
  name,
  onChange,
  onKeyDown,
  placeholder,
  toggleHidden,
  value,
  ...props
}: AuthPasswordInputProps) => {
  const generatedId = React.useId();
  const inputId = props.id ?? `${name}-${generatedId}`;
  const shouldHideLabel = hideLabel ?? Boolean(icon);
  return (
    <AuthInputWrapper>
      {label ? (
        <label className={shouldHideLabel ? 'sr-only' : undefined} htmlFor={inputId}>
          {label}
        </label>
      ) : null}
      {icon ? (
        <span aria-hidden="true" className="leading">
          {icon}
        </span>
      ) : null}
      <div className="input-wrapper">
        <input
          {...props}
          className="text-lg"
          disabled={disabled}
          id={inputId}
          name={name}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder ?? label ?? undefined}
          type={hidden ? 'password' : 'text'}
          value={value}
        />
      </div>
      <button className="hide-password-button text-bzgray-500" onClick={toggleHidden} type="button">
        <Icon color="currentColor" icon={hidden ? faEye : faEyeSlash} />
      </button>
    </AuthInputWrapper>
  );
};

const AuthInputWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  align-items: center;
  padding: 16px 24px 16px 16px;
  gap: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 0.5px solid #e1ebfa;
  background: #f2f8ff;
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .leading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  label {
    font-size: 0.8rem;
    z-index: 2;
    color: #5b7292;
  }
  input {
    width: 100%;
    display: inline-block;
    padding: 0.8rem 0;
    outline: none !important;
    border: 0px solid #fff !important;
    background: transparent;
    color: #5b7292;
    box-shadow: none;
  }
  .hide-password-button {
    width: 20px;
    height: 20px;
  }
  .input-wrapper {
    /* border: solid 1px ${({ theme }) => theme.colorPalette.gray300}; */
    font-size: ${({ theme }) => theme.fontSize.sm};
    overflow: hidden;
    border-radius: 4px;
    width: 100%;
    input {
      line-height: 1.5rem;
      width: 100%;
      padding: 0.25rem 0;
      &:focus {
        outline: none;
      }
    }
  }
`;

export const AuthBoxWrapper = styled.div`
  .action-container {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
    display: inline-flex;
    width: 100%;
    button {
      width: 100%;
      .action-text {
        width: 100%;
        text-align: center;
      }
    }
  }
  .grecaptcha-badge {
    box-shadow: none !important;
  }
`;
